/**
 * @fileoverview Component for uploading ad campaign leads to a selected clinic.
 * This component provides a modal interface for selecting a clinic and
 * uploading campaign leads files (.csv, .xlsx, .xls).
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import { Modal, Button, Upload, message, Row, Col, Icon, Select } from "antd";
import { uploadCampaignLeads } from "../../services/campaigns";
import { getPhysicalClinicList } from "../../services/clinicService";

/**
 * UploadAdCampaignLeads Component
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Controls the visibility of the modal
 * @param {Function} props.onClose - Callback function to close the modal
 * @returns {React.Component} A modal component for uploading campaign leads
 */
const UploadAdCampaignLeads = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [clinicsPage, setClinicsPage] = useState(1);
  const [clinicsSearch, setClinicsSearch] = useState("");
  const [hasMoreClinics, setHasMoreClinics] = useState(true);
  const [loadingMoreClinics, setLoadingMoreClinics] = useState(false);

  // Fetch clinics for the modal (local state)
  const fetchModalClinics = async (search = "", page = 1, perPage = 10) => {
    if (page === 1) setClinics([]);
    setLoadingMoreClinics(true);
    try {
      const response = await getPhysicalClinicList(
        page,
        search,
        false,
        perPage
      );
      if (response.data.success) {
        const { clinics: newClinics, total_records } = response.data.data;
        setClinics(prev =>
          page === 1 ? newClinics : [...prev, ...newClinics]
        );
        setClinicsPage(page);
        setHasMoreClinics(
          (page - 1) * perPage + newClinics.length < total_records
        );
      } else {
        setClinics([]);
        setHasMoreClinics(false);
      }
    } finally {
      setLoadingMoreClinics(false);
    }
  };

  // Initial fetch and reset on open
  React.useEffect(() => {
    if (visible) {
      setClinics([]);
      setClinicsPage(1);
      setClinicsSearch("");
      setHasMoreClinics(true);
      fetchModalClinics();
      setSelectedClinic(null);
      setFileList([]);
    }
  }, [visible]);

  // Debounced search handler
  const handleClinicSearch = debounce(value => {
    setClinicsSearch(value);
    fetchModalClinics(value, 1, 10);
  }, 800);

  // Non-debounced clear handler
  const handleClear = () => {
    setClinicsSearch("");
    fetchModalClinics("", 1, 10);
  };

  // Infinite scroll handler (for dropdownRender)
  const handleDropdownScroll = async e => {
    const { target } = e;
    if (
      target.scrollTop + target.offsetHeight === target.scrollHeight &&
      hasMoreClinics &&
      !loadingMoreClinics
    ) {
      await fetchModalClinics(clinicsSearch, clinicsPage + 1, 10);
    }
  };

  /**
   * Handles the file upload process
   * Validates clinic selection, manages loading state and error handling
   * @param {Object} options - Upload options
   * @param {File} options.file - The file to be uploaded
   * @returns {Promise<void>}
   */
  const handleUpload = async ({ file }) => {
    if (!selectedClinic) {
      message.warning("Please select a clinic first");
      return;
    }
    setLoading(true);
    try {
      await uploadCampaignLeads(selectedClinic, file);
      message.success("Leads uploaded successfully");
      setFileList([]);
      onClose();
    } catch (err) {
      message.error(err?.message || "Failed to upload leads");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Configuration object for the Upload component
   * Handles file selection, removal and customizes upload behavior
   * @type {Object}
   */
  const uploadProps = {
    beforeUpload: file => {
      setFileList([file]);
      return false;
    },
    fileList,
    onRemove: () => setFileList([]),
    customRequest: handleUpload,
    showUploadList: true,
    accept: ".csv,.xlsx,.xls"
  };

  /**
   * Render the component
   * @returns {JSX.Element} - The rendered modal with clinic selection and file upload
   */
  return (
    <Modal
      title="Upload Campaign Leads"
      visible={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Row gutter={8} align="middle">
        <Col span={16}>
          {/* Clinic selection dropdown */}
          <div>
            <label htmlFor="clinic-select">
              Select clinic:
              <Select
                id="clinic-select"
                className="full-width"
                showSearch
                filterOption={false}
                onSearch={handleClinicSearch}
                onChange={setSelectedClinic}
                value={selectedClinic || undefined}
                allowClear
                placeholder="Select clinic"
                onClear={handleClear}
                dropdownStyle={{ maxHeight: 160, overflow: "auto" }}
                dropdownRender={menu => (
                  <div
                    onScroll={handleDropdownScroll}
                    style={{ maxHeight: 160, overflow: "auto" }}
                  >
                    {menu}
                  </div>
                )}
              >
                {clinics.map(clinic => (
                  <Select.Option key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </Select.Option>
                ))}
                {loadingMoreClinics && (
                  <Select.Option key="loading" disabled>
                    <Icon type="loading" /> Loading...
                  </Select.Option>
                )}
              </Select>
            </label>
          </div>
        </Col>
        <Col span={8}>
          {/* File upload dragger component */}
          <Upload.Dragger
            {...uploadProps}
            disabled={loading || !selectedClinic}
            style={{ width: "100%" }}
          >
            <p className="ant-upload-drag-icon">
              <Icon type="inbox" />
            </p>
            <p className="ant-upload-text">Click or drag file to upload</p>
          </Upload.Dragger>
        </Col>
      </Row>
      <div style={{ textAlign: "right", marginTop: 16 }}>
        <Button onClick={onClose} style={{ marginRight: 8 }}>
          Cancel
        </Button>
        <Button
          type="primary"
          loading={loading}
          disabled={!selectedClinic || fileList.length === 0}
          onClick={() => handleUpload({ file: fileList[0] })}
        >
          Upload
        </Button>
      </div>
    </Modal>
  );
};

/**
 * PropTypes for the UploadAdCampaignLeads component
 * Ensures required props are provided with correct types
 */
UploadAdCampaignLeads.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default UploadAdCampaignLeads;
